import { useCallback, useEffect, useMemo, useState } from "react";

import { Box } from "../../components/ui/box";
import { Button, ButtonExpansion } from "../../components/ui/button";
import { Icon, type IconName } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import type { DataTableGroupSortDirection } from "../../hooks/use-data-table-state";

import { useDataTableSurface } from "./data-table-context";
import type { Omittable } from "../../lib/props";

const NONE_LABEL = "None";

export type DataTableGroupByDropdownProps = Omittable<{
  groupByField: string;
  setGroupByField: (field: string) => void;
  groupByLabels: Record<string, string>;
  groupByIcons?: Partial<Record<string, IconName>>;
  groupSortDirection?: DataTableGroupSortDirection;
  setGroupSortDirection?: (direction: DataTableGroupSortDirection) => void;
  /** Suppress the active-field label on the trigger and show only the icon —
   * the same collapsed look the toggle takes on at narrow/mobile widths —
   * even when there's room for the label. Use where the toolbar should stay
   * compact (e.g. a narrow drawer). */
  iconOnly?: boolean;
}>;

const SORT_OPTIONS: Array<{
  value: DataTableGroupSortDirection;
  label: string;
}> = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];

export function DataTableGroupByDropdown({
  groupByField,
  setGroupByField,
  groupByLabels,
  groupByIcons = {},
  groupSortDirection = "asc",
  setGroupSortDirection,
  iconOnly = false,
}: DataTableGroupByDropdownProps) {
  const surface = useDataTableSurface();
  const [localField, setLocalField] = useState(groupByField);
  const [localDirection, setLocalDirection] =
    useState<DataTableGroupSortDirection>(groupSortDirection);

  useEffect(() => {
    setLocalField(groupByField);
  }, [groupByField]);
  useEffect(() => {
    setLocalDirection(groupSortDirection);
  }, [groupSortDirection]);

  const isGrouped = localField !== "none";
  const activeIcon = groupByIcons[localField];

  const emitFieldChange = useCallback(
    (key: string) => {
      setLocalField(key);
      setGroupByField(key);
    },
    [setGroupByField],
  );

  const emitDirectionChange = useCallback(
    (dir: DataTableGroupSortDirection) => {
      setLocalDirection(dir);
      setGroupSortDirection?.(dir);
    },
    [setGroupSortDirection],
  );

  const fieldOptions = useMemo(
    () =>
      Object.entries(groupByLabels).map(([key, label]) => ({
        value: key,
        label: key === "none" ? NONE_LABEL : label,
      })),
    [groupByLabels],
  );

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant={isGrouped ? "primary-outline" : "ghost"}
            size="xs"
            expanded={isGrouped && !iconOnly}
          />
        }
      >
        <Icon name="layers" size="sm" />
        {/* Grouped fields only: the "none" entry must never paint, even for
            the collapse animation's held frame — ButtonExpansion holds the
            last non-null children while the slot slides shut. */}
        <ButtonExpansion>
          {isGrouped ? groupByLabels[localField] : null}
        </ButtonExpansion>
      </PopoverTrigger>
      <PopoverPopup align="start" className="w-auto">
        <Box gap="xs">
          <Text size="sm" variant="muted">
            Group by
          </Text>
          <Box direction="row" align="center" gap="sm">
            <Select
              items={fieldOptions}
              value={localField}
              onValueChange={(value) => {
                if (value != null) emitFieldChange(value);
              }}
            >
              <SelectTrigger size="sm">
                {activeIcon ? (
                  <Icon name={activeIcon} size="sm" color="muted" />
                ) : null}
                <SelectValue placeholder="Select field" />
              </SelectTrigger>
              <SelectPopup sideOffset={4}>
                {fieldOptions.map(({ value, label }) => {
                  const itemIcon = groupByIcons[value];
                  return (
                    <SelectItem key={value} value={value} label={label}>
                      {itemIcon ? (
                        <Box as="span" direction="row" align="center" gap="sm">
                          <Icon name={itemIcon} size="sm" color="muted" />
                          {label}
                        </Box>
                      ) : (
                        label
                      )}
                    </SelectItem>
                  );
                })}
              </SelectPopup>
            </Select>

            <Select
              items={SORT_OPTIONS}
              value={localDirection}
              onValueChange={(value) => {
                if (value != null)
                  emitDirectionChange(value as DataTableGroupSortDirection);
              }}
            >
              <SelectTrigger size="sm">
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectPopup sideOffset={4}>
                {SORT_OPTIONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value} label={label}>
                    {label}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          </Box>
        </Box>
      </PopoverPopup>
    </Popover>
  );
}
